import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import API, { apiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { ArrowLeft, Loader2, Plus, Trash2, ShieldCheck, Save, X, ExternalLink, Power, PowerOff, Pencil } from "lucide-react";
import { toast } from "sonner";

const TYPES = [
    { value: "istituzionale", label: "Istituzionale" },
    { value: "normativa", label: "Normativa" },
    { value: "sentenza", label: "Sentenza" },
    { value: "guida_ministeriale", label: "Guida ministeriale" },
    { value: "documentazione_tecnica", label: "Documentazione tecnica" },
    { value: "altro", label: "Altro" },
];

const EMPTY = { title: "", url: "", description: "", resource_type: "normativa", category_id: null, sort_order: 0 };

export default function AdminResources() {
    const [items, setItems] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY);
    const [saving, setSaving] = useState(false);

    const load = () => { setLoading(true); API.get("/admin/resources").then(({ data }) => setItems(data)).catch(e => setError(apiError(e))).finally(() => setLoading(false)); };
    useEffect(() => { load(); API.get("/categories").then(({ data }) => setCategories(data)).catch(() => {}); }, []);

    const startNew = () => { setForm({ ...EMPTY, sort_order: items.length + 1 }); setEditing("new"); };
    const startEdit = (r) => { setForm({ title: r.title, url: r.url, description: r.description || "", resource_type: r.resource_type, category_id: r.category_id || null, sort_order: r.sort_order || 0 }); setEditing(r.id); };
    const cancel = () => { setEditing(null); setForm(EMPTY); };

    const save = async () => {
        setSaving(true);
        try {
            const payload = { ...form, category_id: form.category_id || null };
            if (editing === "new") { await API.post("/admin/resources", payload); toast.success("Risorsa creata"); }
            else { await API.patch(`/admin/resources/${editing}`, payload); toast.success("Risorsa aggiornata"); }
            cancel(); load();
        } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
    };
    const toggle = async (id) => { try { await API.post(`/admin/resources/${id}/toggle`); load(); } catch (e) { toast.error(apiError(e)); } };
    const remove = async (id) => { try { await API.delete(`/admin/resources/${id}`); toast.success("Risorsa eliminata"); load(); } catch (e) { toast.error(apiError(e)); } };

    const canSave = form.title.length >= 3 && /^https?:\/\/.+/i.test(form.url) && !saving;

    return (
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-10">
            <Button asChild variant="ghost" size="sm" className="-ml-2 mb-2 text-muted-foreground">
                <Link to="/admin"><ArrowLeft className="size-4 mr-1" /> Panoramica admin</Link>
            </Button>
            <div className="text-xs uppercase tracking-widest text-primary font-semibold inline-flex items-center gap-1.5">
                <ShieldCheck className="size-3.5" /> Pannello amministratore
            </div>
            <div className="mt-1 flex items-center justify-between">
                <h1 className="font-serif text-3xl font-semibold" data-testid="admin-res-title">Risorse esterne</h1>
                {editing === null && <Button onClick={startNew} data-testid="admin-res-new"><Plus className="size-4 mr-2" /> Nuova risorsa</Button>}
            </div>

            {error && <Alert variant="destructive" className="mt-4"><AlertDescription>{error}</AlertDescription></Alert>}

            {editing !== null && (
                <Card className="mt-6 border-primary/30" data-testid="admin-res-form">
                    <CardContent className="pt-6 space-y-4">
                        <div className="space-y-2">
                            <Label>Titolo *</Label>
                            <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} maxLength={140} data-testid="res-title" />
                        </div>
                        <div className="space-y-2">
                            <Label>URL * <span className="text-xs text-muted-foreground">(deve iniziare con http:// o https://)</span></Label>
                            <Input value={form.url} onChange={e => setForm({ ...form, url: e.target.value })} placeholder="https://…" data-testid="res-url" />
                        </div>
                        <div className="space-y-2">
                            <Label>Descrizione</Label>
                            <Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={2} maxLength={500} data-testid="res-desc" />
                        </div>
                        <div className="space-y-2">
                            <Label>Ordine</Label>
                            <Input type="number" value={form.sort_order} onChange={e => setForm({ ...form, sort_order: parseInt(e.target.value, 10) || 0 })} data-testid="res-order" />
                        </div>
                        <div className="flex justify-end gap-2 pt-2 border-t">
                            <Button variant="ghost" onClick={cancel}><X className="size-4 mr-1" /> Annulla</Button>
                            <Button onClick={save} disabled={!canSave} data-testid="res-save">
                                {saving ? <Loader2 className="size-4 mr-2 animate-spin" /> : <Save className="size-4 mr-2" />}
                                Salva
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            )}

            <div className="mt-6 space-y-2" data-testid="admin-res-list">
                {loading ? <div className="text-center py-10"><Loader2 className="size-6 mx-auto animate-spin text-muted-foreground" /></div>
                    : items.length === 0 ? <p className="text-center py-10 text-muted-foreground">Nessuna risorsa. Aggiungine una per iniziare.</p>
                        : items.map(r => (
                            <div key={r.id} className={`flex items-start gap-3 rounded-xl border bg-white p-4 ${r.is_active ? "border-slate-200" : "border-slate-200 opacity-60"}`} data-testid={`res-${r.id}`}>
                                <span className="size-8 rounded-full bg-primary/10 text-primary text-xs font-semibold flex items-center justify-center shrink-0">{r.sort_order}</span>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                                        <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 font-semibold">{TYPES.find(t => t.value === r.resource_type)?.label || r.resource_type}</span>
                                        {r.category && <span className="text-xs text-primary/80 uppercase tracking-widest font-semibold">{r.category.name}</span>}
                                        {!r.is_active && <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">Disattivata</span>}
                                    </div>
                                    <div className="font-semibold text-slate-900">{r.title}</div>
                                    <a href={r.url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline inline-flex items-center gap-1 mt-1">
                                        {r.url} <ExternalLink className="size-3" />
                                    </a>
                                    {r.description && <p className="text-xs text-muted-foreground mt-1">{r.description}</p>}
                                </div>
                                <div className="flex gap-1">
                                    <Button size="sm" variant="ghost" onClick={() => toggle(r.id)} data-testid={`res-toggle-${r.id}`} title={r.is_active ? "Disattiva" : "Attiva"}>
                                        {r.is_active ? <PowerOff className="size-4" /> : <Power className="size-4" />}
                                    </Button>
                                    <Button size="sm" variant="ghost" onClick={() => startEdit(r)} data-testid={`res-edit-${r.id}`}><Pencil className="size-4" /></Button>
                                    <AlertDialog>
                                        <AlertDialogTrigger asChild><Button size="sm" variant="ghost" className="text-destructive hover:text-destructive" data-testid={`res-delete-${r.id}`}><Trash2 className="size-4" /></Button></AlertDialogTrigger>
                                        <AlertDialogContent>
                                            <AlertDialogHeader>
                                                <AlertDialogTitle>Eliminare "{r.title}"?</AlertDialogTitle>
                                                <AlertDialogDescription>Operazione irreversibile.</AlertDialogDescription>
                                            </AlertDialogHeader>
                                            <AlertDialogFooter>
                                                <AlertDialogCancel>Annulla</AlertDialogCancel>
                                                <AlertDialogAction onClick={() => remove(r.id)} className="bg-destructive text-destructive-foreground">Elimina</AlertDialogAction>
                                            </AlertDialogFooter>
                                        </AlertDialogContent>
                                    </AlertDialog>
                                </div>
                            </div>
                        ))}
            </div>
        </div>
    );
}
